"use client";

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Plus, Clock } from "lucide-react";
import { Staff, StaffRole } from "@/types";
import { toast } from "sonner";
import { userService } from "@/services/userService";
import CommonHeader from "./common/CommonHeader";
import { StaffSearchBar } from "./staff/StaffSearchBar";
import { StaffTable } from "./staff/StaffTable";
import { StaffFormDialog, StaffFormData, StaffUpdateFormData } from "./staff/StaffFormDialog";
import { StaffDetailDialog } from "./staff/StaffDetailDialog";
import { StaffDeleteDialog } from "./staff/StaffDeleteDialog";
import { Button } from "./ui/button";

export function StaffManagement() {
  const navigate = useNavigate();
  const { appointments } = useApp();

  // Local state for API-backed staff
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);

  const [formData, setFormData] = useState<StaffFormData>({
    name: "",
    phone: "",
    email: "",
    password: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch staff from API
  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAllStaffs();

      // Map API response to UI Staff type
      const mappedStaff: Staff[] = data.map((user) => ({
        id: user.id,
        name: user.name,
        phone: user.phone, // Phone is guaranteed to be a string from the API
        email: user.email || undefined,
        role: (user.staffRole || "technician") as StaffRole, // From backend
        status: (user.Staff[0]?.isDeleted ? "inactive" : "active") as "active" | "inactive", // From backend
        joinedDate: new Date(user.createdAt),
        totalJobs: user.totalJobs || 0, // From backend
        notes: user.notes || undefined, // From backend
      }));

      setStaff(mappedStaff);
    } catch (err) {
      setError("Failed to load staff. Please try again.");
      console.error("Error fetching staff:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered staff
  const filteredStaff = useMemo(() => {
    return staff
      .filter((member) => member.status === "active")
      .filter(
        (member) =>
          member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          member.phone.includes(searchTerm) ||
          member.email?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
  }, [staff, searchTerm]);

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      password: "",
    });
  };

  const handleAdd = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.password) {
      toast.error("Password is required");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!formData.phone.trim()) {
      toast.error("Phone is required");
      return;
    }

    try {
      setIsSubmitting(true);

      // Call API to create user
      const newUser = await userService.createUser({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: "STAFF",
        phone: formData.phone,
      });

      // Map the new user to Staff type and add to state immediately
      const newStaff: Staff = {
        id: newUser.id,
        name: newUser.name,
        phone: newUser.phone,
        email: newUser.email || undefined,
        role: "technician",
        status: "active" as const,
        joinedDate: new Date(newUser.createdAt),
        totalJobs: 0,
        notes: undefined,
      };

      // Update state immediately without refetching
      setStaff((prev) => [...prev, newStaff]);

      toast.success("Staff member added successfully");

      setIsAddDialogOpen(false);
      resetForm();
    } catch (err: any) {
      console.error("Error creating staff:", err);
      const responseData = err.response?.data;
      const errorCode = responseData?.errors?.code as string | undefined;

      const errorMessage =
        (errorCode === "PHONE_EXISTS"
          ? "Phone number already exists. Please use a different phone number."
          : errorCode === "EMAIL_EXISTS"
            ? "Email address already exists. Please use a different email address."
            : responseData?.message) ||
        "Failed to create staff member. Please try again.";

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedStaff) return;

    // Validation for edit
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.phone.trim()) {
      toast.error("Phone is required");
      return;
    }

    try {
      // Build update payload with only password if it's been typed
      const updatePayload: StaffUpdateFormData & { password?: string } = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
      };

      // Only include password if it's not empty
      if (formData.password.trim()) {
        updatePayload.password = formData.password;
      }

      await userService.staffUpdate(selectedStaff.id, updatePayload);

      setStaff((prev) =>
        prev.map((member) =>
          member.id === selectedStaff.id
            ? {
                ...member,
                name: formData.name,
                phone: formData.phone,
                email: formData.email || undefined,
              }
            : member
        )
      );
    } catch (err) {
      console.error("Error updating staff:", err);
      toast.error("Failed to update staff member. Please try again.");
    }

    // Client-side update to local state (for now, until backend update endpoint is available)

    toast.success("Staff member updated successfully");
    setIsEditDialogOpen(false);
    setSelectedStaff(null);
    resetForm();
  };

  const handleDelete = async () => {
    if (!selectedStaff) return;

    try {
      // Call the API to delete the staff
      await userService.deleteCustomer(selectedStaff.id); // Using deleteCustomer as it deletes users

      // Remove from local state
      setStaff((prev) =>
        prev.filter((member) => member.id !== selectedStaff.id)
      );

      toast.success("Staff member deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedStaff(null);
    } catch (err) {
      console.error("Error deleting staff:", err);
      toast.error("Failed to delete staff member. Please try again.");
    }
  };

  const openEditDialog = (member: Staff) => {
    setSelectedStaff(member);
    setFormData({
      name: member.name,
      phone: member.phone,
      email: member.email || "",
      password: "", // Password not needed for edit
    });
    setIsEditDialogOpen(true);
  };

  const openDetailDialog = (member: Staff) => {
    setSelectedStaff(member);
    setIsDetailDialogOpen(true);
  };

  const openDeleteDialog = (member: Staff) => {
    setSelectedStaff(member);
    setIsDeleteDialogOpen(true);
  };

  // Get staff statistics
  const getStaffStats = (staffId: string) => {
    const staffAppointments = appointments.filter(
      (apt) => apt.staffId === staffId
    );
    const completed = staffAppointments.filter(
      (apt) => apt.status === "completed"
    ).length;
    const upcoming = staffAppointments.filter(
      (apt) =>
        apt.status === "confirmation-pending" || apt.status === "confirmed"
    ).length;

    return { total: staffAppointments.length, completed, upcoming };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Staff Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your team members</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate("/staff-time-tracking")}
            variant="outline"
            size="default"
            className="shadow-sm"
          >
            <Clock className="mr-2 h-4 w-4" />
            Time Tracking
          </Button>
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            size="default"
            className="shadow-sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Staff Member
          </Button>
        </div>
      </div>

      {/* Search */}
      <StaffSearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">
          Loading staff...
        </div>
      ) : error ? (
        /* Error State */
        <div className="space-y-4">
          <div className="text-center py-8 text-destructive">{error}</div>
          <div className="text-center">
            <Button onClick={fetchStaff} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      ) : (
        /* Staff Table */
        <StaffTable
          staff={filteredStaff}
          appointments={appointments}
          getStaffStats={getStaffStats}
          onView={openDetailDialog}
          onEdit={openEditDialog}
          onDelete={openDeleteDialog}
        />
      )}

      {/* Add Staff Dialog */}
      <StaffFormDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleAdd}
        onCancel={() => {
          setIsAddDialogOpen(false);
          resetForm();
        }}
        mode="add"
        isSubmitting={isSubmitting}
      />

      {/* Edit Staff Dialog */}
      <StaffFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleEdit}
        onCancel={() => {
          setIsEditDialogOpen(false);
          resetForm();
        }}
        mode="edit"
        isSubmitting={false}
      />

      {/* Staff Detail Dialog */}
      <StaffDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        staff={selectedStaff}
        appointments={appointments}
        getStaffStats={getStaffStats}
      />

      {/* Delete Confirmation Dialog */}
      <StaffDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        staff={selectedStaff}
        onConfirm={handleDelete}
      />
    </div>
  );
}
